import { useEffect, useMemo, useState } from "react";
import { handleError } from "src/Common/function";
import AccountService from "src/Services/API/account.service";
import BranchService from "src/Services/API/branch.service";
import UserService from "src/Services/API/user.service";
import MultiSelectChipInput from "./MultiSelectChipInput";

// Roles whose users appear in the Technician filter. Mirrors the user set the
// hours-worked report builds its rows from (technicians + managers).
const TECHNICIAN_ROLES = new Set(["Technician", "Manager", "Project Manager"]);

// True when any of the user's roles is a technician-type role. Roles is a
// many-to-many array, so checking only roles[0] would miss users whose
// technician role isn't first.
const isTechnicianUser = (user) =>
  user.isEnabled && (user.roles || []).some((role) => TECHNICIAN_ROLES.has(role?.name));

// Cache the fetched option lists across (un)mounts. The section unmounts when
// the modal moves to its preview step and re-mounts on "Back to Filters";
// seeding from this cache means the chips/options are present immediately,
// instead of flashing empty (and tripping the prune-out-of-scope effects)
// while the data refetches.
const optionCache = { accounts: [], branches: [], users: [] };

/**
 * Shared Customer / Branch / Location / Technician filter block for the ad-hoc
 * report modals.
 *
 * Customer  -> Account (top-level). Selecting customers also scopes which
 *              Locations are offered.
 * Location  -> CustomerEntity (a specific location under an account).
 * Branch    -> BranchEntity.
 * Technician-> enabled users with a technician/manager role.
 *
 * value:    { customerIds, branchIds, locationIds, technicianIds }
 * onChange(next): called with the full next filter value on any change
 * shown:     gates the data fetch so options load only while the modal is open
 */
const ReportFiltersSection = ({ value, onChange, shown }) => {
  const { customerIds = [], branchIds = [], locationIds = [], technicianIds = [] } = value;

  // Seed from the cross-mount cache so a re-mount (e.g. returning from preview)
  // renders the previously-loaded options immediately.
  const [accounts, setAccounts] = useState(optionCache.accounts);
  const [branches, setBranches] = useState(optionCache.branches);
  // Technicians are fetched here (branch-independent) rather than from the
  // passed-in userArray, which the report center pre-filters to the URL branch
  // and would otherwise hide technicians from other branches on initial load.
  const [users, setUsers] = useState(optionCache.users);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);

  useEffect(() => {
    if (!shown) return;
    setLoadingAccounts(true);
    AccountService.getAll()
      .then((response) => {
        const data = response.data || [];
        optionCache.accounts = data;
        setAccounts(data);
      })
      .catch((error) => handleError(error))
      .finally(() => setLoadingAccounts(false));

    setLoadingBranches(true);
    BranchService.getAll()
      .then((response) => {
        const data = response.data || [];
        optionCache.branches = data;
        setBranches(data);
      })
      .catch((error) => handleError(error))
      .finally(() => setLoadingBranches(false));

    // branchId 0 = all branches (no branch filter).
    setLoadingUsers(true);
    UserService.getAll(0)
      .then((response) => {
        const data = response.data || [];
        optionCache.users = data;
        setUsers(data);
      })
      .catch((error) => handleError(error))
      .finally(() => setLoadingUsers(false));
  }, [shown]);

  // Customer (account) options.
  const customerOptions = useMemo(
    () =>
      accounts
        .map((account) => ({ id: account.id, name: account.name }))
        .sort((a, b) => (a.name || "").localeCompare(b.name || "")),
    [accounts],
  );

  // Location (customer) options. When customers are selected, only their
  // locations are offered; otherwise every location is selectable.
  const locationOptions = useMemo(() => {
    const customerSet = new Set(customerIds);
    const inScope = customerIds.length ? accounts.filter((account) => customerSet.has(account.id)) : accounts;
    const locations = inScope.flatMap((account) => account.customers || []);
    return locations
      .map((location) => ({
        id: location.id,
        name: location.companyName || "Location",
        subtitle: location.branch?.name,
      }))
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [accounts, customerIds]);

  // Drop any selected locations that fall out of scope when the customer
  // selection changes. Skip while accounts are still loading (e.g. right after
  // re-mounting from the preview step) so we don't clear valid selections
  // against an empty list before the data arrives.
  useEffect(() => {
    if (!locationIds.length || loadingAccounts || accounts.length === 0) return;
    const allowed = new Set(locationOptions.map((option) => option.id));
    const next = locationIds.filter((id) => allowed.has(id));
    if (next.length !== locationIds.length) {
      onChange({ ...value, locationIds: next });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationOptions, loadingAccounts]);

  const branchOptions = useMemo(() => branches.map((branch) => ({ id: branch.id, name: branch.name })), [branches]);

  // Technician options, scoped to the selected branches: a technician is shown
  // only when one of their branches is in the selection. No branch selected =
  // all technicians.
  const technicianOptions = useMemo(() => {
    const branchSet = new Set(branchIds);
    return users
      .filter(isTechnicianUser)
      .filter((user) => branchSet.size === 0 || (user.branches || []).some((branch) => branchSet.has(branch.id)))
      .map((user) => ({ id: user.id, name: user.name, subtitle: user.email }))
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [users, branchIds]);

  // Drop any selected technicians that fall out of scope when the branch
  // selection changes. Skip while users are still loading (e.g. right after
  // re-mounting from the preview step) so we don't clear valid selections
  // against an empty list before the data arrives.
  useEffect(() => {
    if (!technicianIds.length || loadingUsers || users.length === 0) return;
    const allowed = new Set(technicianOptions.map((option) => option.id));
    const next = technicianIds.filter((id) => allowed.has(id));
    if (next.length !== technicianIds.length) {
      onChange({ ...value, technicianIds: next });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [technicianOptions, loadingUsers]);

  const update = (patch) => onChange({ ...value, ...patch });

  return (
    <div className="flex flex-col gap-6">
      <MultiSelectChipInput
        label="Customer"
        options={customerOptions}
        selectedIds={customerIds}
        onChange={(ids) => update({ customerIds: ids })}
        placeholder="Search customers..."
        allLabel="All Customers"
        loading={loadingAccounts}
      />
      <MultiSelectChipInput
        label="Branch"
        options={branchOptions}
        selectedIds={branchIds}
        onChange={(ids) => update({ branchIds: ids })}
        placeholder="Search branches..."
        allLabel="All Branches"
        loading={loadingBranches}
      />
      <MultiSelectChipInput
        label="Location"
        options={locationOptions}
        selectedIds={locationIds}
        onChange={(ids) => update({ locationIds: ids })}
        placeholder="Search locations..."
        allLabel="All Locations"
        loading={loadingAccounts}
      />
      <MultiSelectChipInput
        label="Technician"
        options={technicianOptions}
        selectedIds={technicianIds}
        onChange={(ids) => update({ technicianIds: ids })}
        placeholder="Search technicians..."
        allLabel="All Technicians"
        loading={loadingUsers}
      />
    </div>
  );
};

export const EMPTY_REPORT_FILTERS = {
  customerIds: [],
  branchIds: [],
  locationIds: [],
  technicianIds: [],
};

export default ReportFiltersSection;
